import { useCallback, useState } from "react";

export type DemoPackage = "starter" | "professional" | "enterprise";

// A local sales presentation control, never a subscription or authorization rule.
export const IS_LOCAL_DEMO = import.meta.env.VITE_LOCAL_DEMO === "true";
const STORAGE_KEY = "cd_local_demo_package_v1";

export const DEMO_PACKAGES = [
  { id: "starter", label: "Starter", shortLabel: "Starter", price: "$299", description: "Windrows, recipes and field logs" },
  { id: "professional", label: "Professional", shortLabel: "Pro", price: "$599", description: "Tracking + daily operations" },
  { id: "enterprise", label: "Enterprise", shortLabel: "Enterprise", price: "$1,499", description: "Compost + quotes, invoices and collection" },
] as const;

export function useDemoPackage() {
  const [plan, setPlan] = useState<DemoPackage>(() => {
    if (!IS_LOCAL_DEMO) return "starter";
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      return DEMO_PACKAGES.some((item) => item.id === saved) ? saved as DemoPackage : "starter";
    } catch { return "starter"; }
  });
  const selectPlan = useCallback((next: DemoPackage) => {
    if (!IS_LOCAL_DEMO) return;
    setPlan(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* A preview still works without storage. */ }
  }, []);
  return [plan, selectPlan] as const;
}

const CORE_PATHS = ["/app/windrows", "/app/recipes", "/app/ingredients", "/app/log-temp", "/app/log-turn", "/app/log-material", "/app/operator"];
const OPERATIONS_PATHS = ["/app/ops/bols", "/app/ops/jobs", "/app/ops/clients", "/app/ops/products", "/app/ops/inventory", "/app/ops/incidents", "/app/ops/work-orders"];
const ADMIN_PATHS = ["/app/team", "/app/settings"];
const ENTERPRISE_SAMPLE_PATHS = ["/app/enterprise"];

export function isDemoPathVisible(pathname: string, plan: DemoPackage) {
  const paths = [
    ...CORE_PATHS,
    ...(plan !== "starter" ? OPERATIONS_PATHS : []),
    ...(plan === "enterprise" ? [...ADMIN_PATHS, ...ENTERPRISE_SAMPLE_PATHS] : []),
  ];
  return paths.some((path) => pathname === path || pathname.startsWith(path + "/"));
}

export default function DemoPackageSwitch({ plan, onChange, collapsed }: {
  plan: DemoPackage;
  onChange: (plan: DemoPackage) => void;
  collapsed: boolean;
}) {
  const selected = DEMO_PACKAGES.find((item) => item.id === plan)!;
  return (
    <div className={`border-b border-sidebar-border ${collapsed ? "px-2 py-3" : "px-3 py-3"}`}>
      {!collapsed && <p className="text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/50 mb-2">Demo package view</p>}
      <div role="group" aria-label="Demo package view" className={`flex gap-1 rounded-lg bg-black/15 p-1 ${collapsed ? "flex-col" : ""}`}>
        {DEMO_PACKAGES.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`${item.label} demo, ${item.price} per month`}
            aria-pressed={plan === item.id}
            title={`${item.label} · ${item.price}/month · ${item.description}`}
            onClick={() => onChange(item.id)}
            className={`flex-1 rounded-md px-1 py-1.5 text-[10px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-300 ${plan === item.id ? "bg-forest-600 text-white shadow-sm" : "text-sidebar-foreground/60 hover:text-white hover:bg-sidebar-hover"}`}
          >
            {collapsed ? index + 1 : item.shortLabel}
          </button>
        ))}
      </div>
      {!collapsed && (
        <div className="mt-2 px-1">
          <p className="flex items-center justify-between gap-2 text-[11px] text-white/90"><span className="font-semibold">{selected.label}</span><span>{selected.price}<span className="text-sidebar-foreground/50">/mo</span></span></p>
          <p className="mt-0.5 text-[10px] text-sidebar-foreground/60">{selected.description}</p>
          <p className="mt-1 text-[9px] text-sidebar-foreground/45" title="Published monthly prices; the module grouping is proposed for this demo. Selecting a view does not change permissions, billing or the subscription.">Proposed demo scope · monthly pricing</p>
        </div>
      )}
    </div>
  );
}
